import React from 'react';
import PropTypes from 'prop-types';
import { FieldArray } from 'react-final-form-arrays';
import { Field, FormSpy } from 'react-final-form';
import GenericTextInputField from '../../../commons/form-fields/GenericTextInputField';
import GenericToggleButtonGroupField from '../../../commons/form-fields/GenericToggleButtonGroupField';
import { trueFalseOptions } from '../../OnboardingConstants';
import GenericTextAreaInputField from '../../../commons/form-fields/GenericTextAreaInputField';
import HeaderAndError from '../../../commons/form-fields/HeaderAndError';
import { validateDates } from '../../../commons/utils';
import { ErrorMessage } from '../../../commons/form-fields/ErrorMessage';

const SingleEmploymentFields = ({
    name,
    index,
    isCurrentEmployer,
    empCount,
    fields,
}) => (
    <div className="w-auto mx-auto bg-gray-100 border-gray-200 focus:border-gray-700 border-2 px-2 md:px-6 py-1 md:mt-3 md:mb-6 rounded text-sm">
        <GenericTextInputField
            name={`${name}[${index}].name`}
            label={`${empCount}. Client/Employer Name`}
            type="text"
            required
        />
        <GenericToggleButtonGroupField
            name={`${name}[${index}].isPublicCompany`}
            inlineLabel={'Is this a Public Company?'}
            options={trueFalseOptions}
            required
        />
        <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="col-span-3 sm:col-span-1">
                <GenericTextInputField
                    name={`${name}[${index}].title`}
                    label={'Job Title'}
                    type={'text'}
                    required
                />
            </div>
            <div className="col-span-3 sm:col-span-1">
                <GenericTextInputField
                    name={`${name}[${index}].startDate`}
                    label={'Start Date'}
                    type="date"
                    validateAndRequiredDate
                />
            </div>
            <div className="col-span-3 sm:col-span-1">
                {!isCurrentEmployer && (
                    <GenericTextInputField
                        name={`${name}[${index}].endDate`}
                        label={'End Date'}
                        type="date"
                        validateAndRequiredDate
                    />
                )}
            </div>
        </div>
        <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="col-span-3 sm:col-span-1">
                <GenericTextInputField
                    name={`${name}[${index}].location`}
                    label={'Location'}
                    type="text"
                    placeholder="Seattle, Washington, USA"
                />
            </div>
        </div>
        <GenericTextAreaInputField
            name={`${name}[${index}].description`}
            label={'Job Description'}
        />
        <Field
            name={`${name}[${index}].isCurrentEmployer`}
            render={() => <div />}
        />
        <div>
            <button
                className="text-teal-600 hover:text-teal-700 underline font-bold py-1 px-3 rounded place-content-end mb-2 md:mb-4"
                type="button"
                onClick={() => {
                    fields.remove(index);
                }}
                data-testid={`info.resume.employers[${index}].removeJob`}
            >
                Remove Job
            </button>
        </div>
    </div>
);

SingleEmploymentFields.propTypes = {
    name: PropTypes.string,
    index: PropTypes.number,
    isCurrentEmployer: PropTypes.bool,
    empCount: PropTypes.number,
    fields: PropTypes.array,
};

class PreEmploymentInfoComponentInner extends React.Component {
    // eslint-disable-next-line no-unused-vars
    getSnapshotBeforeUpdate(prevProps, prevState) {
        const prevValue = prevProps.values.info?.resume?.isCurrentlyEmployed;
        const currValue = this.props.values.info?.resume?.isCurrentlyEmployed;
        const prevEmployersValue = this.props.values.info?.resume?.employers;
        let employers = prevEmployersValue;

        // if value has changed.
        if (prevValue !== currValue) {
            // and prevValue was missing, then setting the new employers
            if (prevValue === undefined || prevValue === null) {
                // if employers is already populated via linkedin parser or any other mean
                // don't reset the values. else, depending on currValue (which won't be null given prevValue was null or undefined,
                // and currentValue isn't equal to prevValue), set employers accordingly.
                employers =
                    employers ??
                    (currValue
                        ? [
                              { isCurrentEmployer: true },
                              { isCurrentEmployer: false },
                          ]
                        : [{ isCurrentEmployer: false }]);
            } else if (prevValue === false) {
                // prev value is false
                // currValue could be true/undefined (since current value could also be deselected).
                employers = prevEmployersValue ?? [
                    { isCurrentEmployer: false },
                ];

                // if currValue is not undefined, it means it is true because prevValue was false.
                // and if employers already doesn't have any current employer
                // add a blank entry for currentEmployer.
                if (
                    currValue &&
                    employers.filter((employer) => employer.isCurrentEmployer)
                        .length === 0
                ) {
                    employers.unshift({
                        isCurrentEmployer: true,
                    });
                }
            } else {
                // prevValue is True
                // currValue could be False/undefined. (since current value could also be deselected)
                // filter out past employers.
                employers = (prevEmployersValue ?? []).filter(
                    (obj) => !obj.isCurrentEmployer
                );
            }
            // update employers.
            this.props.form.change('info.resume.employers', employers);
        }

        return null;
    }

    render() {
        const isCurrentlyEmployed =
            this.props.values.info?.resume?.isCurrentlyEmployed;
        let currentEmpCount = 0;
        let pastEmpCount = 0;

        return (
            <FieldArray
                name={'info.resume.employers'}
                validate={validateDates}
                render={({ fields, meta }) => (
                    <div>
                        {isCurrentlyEmployed && (
                            <div data-testid="current-employment-information">
                                <HeaderAndError
                                    label="Current Employment Information"
                                    style="block text-lg text-teal-700 font-bold pl-1"
                                />
                                {/* eslint-disable-next-line array-callback-return,consistent-return */}
                                {(fields.value ?? []).map((field, index) => {
                                    if (field?.isCurrentEmployer) {
                                        // eslint-disable-next-line no-return-assign
                                        return (
                                            <SingleEmploymentFields
                                                key={index}
                                                name={fields.name}
                                                index={index}
                                                isCurrentEmployer={true}
                                                empCount={
                                                    (currentEmpCount += 1)
                                                }
                                                fields={fields}
                                            />
                                        );
                                    }
                                })}
                                <button
                                    className="text-teal-600 hover:text-teal-700 underline font-bold mt-2 md:mt-0 py-1 px-4 rounded mb-4"
                                    type="button"
                                    onClick={() =>
                                        fields.push({
                                            isCurrentEmployer: true,
                                        })
                                    }
                                    data-testid="add-current-job"
                                >
                                    + Add Job
                                </button>
                            </div>
                        )}

                        {isCurrentlyEmployed !== undefined && (
                            <div data-testid="past-employment-information">
                                <HeaderAndError
                                    label="Past Employment Information"
                                    style="block text-lg text-teal-700 font-bold pl-1"
                                />
                                <label className="block text-sm text-teal-700 italic pl-1">
                                    Please identify all former employers and
                                    clients.
                                </label>
                                {/* eslint-disable-next-line array-callback-return,consistent-return */}
                                {(fields.value ?? []).map((field, index) => {
                                    if (!field?.isCurrentEmployer) {
                                        // eslint-disable-next-line no-return-assign
                                        return (
                                            <SingleEmploymentFields
                                                key={index}
                                                name={fields.name}
                                                index={index}
                                                isCurrentEmployer={false}
                                                empCount={(pastEmpCount += 1)}
                                                fields={fields}
                                            />
                                        );
                                    }
                                })}

                                <button
                                    className="text-teal-600 hover:text-teal-700 underline font-bold mt-2 md:mt-0 py-1 px-4 rounded"
                                    type="button"
                                    onClick={() =>
                                        fields.push({
                                            isCurrentEmployer: false,
                                        })
                                    }
                                    data-testid="add-past-job"
                                >
                                    + Add Job
                                </button>
                            </div>
                        )}

                        {
                            // This error is at field-array level and should only be displayed when error is of type
                            // string. Which would only be when  start date is greater than end date or sevenYearEmploymentHistoryValidation fails.
                            // For this case, error would be of string type.

                            // In case, validation of individual fields fails, error would contain
                            // an array containing error objects per element/field level which should not be rendered
                            // at field array label.

                            // Start Date and End Date validation on field-array level as of now
                            // TODO: Validate on object level
                            typeof meta.error === 'string' && (
                                <ErrorMessage meta={meta} />
                            )
                        }
                    </div>
                )}
            />
        );
    }
}

PreEmploymentInfoComponentInner.propTypes = {
    values: PropTypes.any,
    form: PropTypes.any,
};

const PreEmploymentInfoComponent = (props) => (
    <FormSpy {...props} component={PreEmploymentInfoComponentInner} />
);

export { PreEmploymentInfoComponent };
